import { useSyncExternalStore } from 'react';
import { catalogByPath } from './catalog';
import type { CatalogNote } from './catalog';
import { getVaultState, subscribeVault } from '../session/vaultStore';

/**
 * ONE read of the catalog, shared by every surface that joins against it.
 *
 * The same reasoning as [planStore], and the same cost: 98 shards and ~19,000
 * rows parsed. Thoughts wants the visibility word, folders wants the tag
 * edges, and both are in the row — so reading it twice would parse the whole
 * catalog twice to answer two halves of one question.
 *
 * The empty map is not a loading state anyone has to wait on. A path the map
 * does not know reads as visible and unfiled, which is what every consumer
 * already does for a note catalogued before its columns existed — so a surface
 * fills in rather than blanking while shards decrypt.
 */

export type CatalogMap = ReadonlyMap<string, CatalogNote>;

const EMPTY: CatalogMap = new Map();

let map: CatalogMap = EMPTY;
const listeners = new Set<() => void>();
let generation = 0;

function publish(next: CatalogMap): void {
  map = next;
  for (const listener of listeners) listener();
}

function load(): void {
  generation += 1;
  const mine = generation;
  const vault = getVaultState();
  if (vault.status !== 'ready') return publish(EMPTY);

  void catalogByPath(vault.vault)
    .then((next) => {
      if (mine === generation) publish(next);
    })
    .catch(() => {
      // The catalog is derived and every reader treats absence as "does not
      // know", so a failed read costs joins rather than the screen.
    });
}

subscribeVault(load);
load();

export function getCatalog(): CatalogMap {
  return map;
}

export function subscribeCatalog(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function useCatalog(): CatalogMap {
  return useSyncExternalStore(subscribeCatalog, getCatalog, getCatalog);
}
